import { useEffect, useState } from "react";
import MenuItem from "@Components/Header/MenuItem";
import HowItWorks from "@Icons/HowItWorks";
import {
  HELP_URL,
  HOW_IT_WORKS_URL,
  WHATSAPP_URL,
  FACEBOOK_MESSENGER_URL,
  PHONE_1,
  PHONE_2,
  PHONE_3,
  PHONE_4,
  EMAIL_1,
} from "constants/configConstants";
import openChatWidget from "helpers/openChatWidget";
import config from "@Config/index";
import classes from "./Support.module.scss";
import {
  COMPONENTS_CATEGORY,
  EVENT_LABEL,
  FLOATING_BUTTON_ENTRY_POINTS,
  GROWTH_BOOK_FEATURE_FLAG_NAMES,
  PAGE,
} from "constants/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { AppState } from "@Store/index";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import WechatGreenOutline from "@Icons/WechatGreenOutline";
import useTranslationHook from "hooks/useTranslationHook";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { addLoadZendeskParamInURL } from "@Pages/PropertyPage/data/helper";
import LoginDispatcher from "@Components/Login/actions";
import useWhitelabel from "hooks/useWhitelabel";
// import MessengerIcon from "@Icons/MessengerIcon";
import HowItWorksIcon from "@Icons/HowItWorksIcon";
import useIsDesktop from "hooks/useIsDesktop";
import getWeComUrlBasedOnSource, {
  HK_WEIXIN_DWEB,
  HK_WEIXIN_MWEB,
} from "helpers/getWeComUrlBasedOnSource";
import Image from "@Components/Image";
import getImagePath from "@Utils/getImagePath";
import WeChatIcon from "@Icons/WeChatIcon";
import AgentContainer from "@Pages/Support/Component/AgentContainer";
import { SmartFilterLogo } from "@Pages/Search/components/FilterSection/components/SmartFilters/icons";
import MessengerColorIcon from "@Icons/MessengerColorIcon";
import WhatsAppColorFilled from "@Icons/WhatsAppColorFilled";
import PhoneIconColor from "@Icons/PhoneIconColor";
import EnvelopeColor from "@Icons/EnvelopeColor";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ChatIcon from "@Icons/ChatIcon";

const Support = ({ hideQuickChat = false }: { hideQuickChat?: boolean }) => {
  const {
    QUICK_CHAT_CLICKED,
    HELP_CENTRE_CLICKED,
    HOW_IT_WORKS_CLICKED,
    WHATSAPP_CLICKED,
    FACEBOOK_CLICKED,
    PHONE_CLICKED,
    EMAIL_CLICKED,
    WECHAT_CLICKED,
  } = EVENT_LABEL;
  const { country, isUserLoggedIn, isLFOneFilled } = useSelector(
    ({ appData, leadFormModal }: AppState) => ({
      country: appData.country,
      isUserLoggedIn: appData.isUserLoggedIn,
      isLFOneFilled: leadFormModal.isLFOneFilled,
    }),
    shallowEqual,
  );

  const { isWhitelabel, partner_id } = useWhitelabel();
  const isDesktop = useIsDesktop();
  const [weChatSupportRedirectTo, setWeChatSupportRedirectTo] = useState(
    isDesktop ? HK_WEIXIN_DWEB : HK_WEIXIN_MWEB,
  );
  useEffect(() => {
    const { URLDweb, URLMweb } = getWeComUrlBasedOnSource();
    setWeChatSupportRedirectTo(isDesktop ? URLDweb : URLMweb);
  }, [isDesktop]);
  const dispatch = useDispatch();
  const isZendeskAfterLogin = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ZENDESK_LOGIN_POPUP);
  const isAmberCopilotEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT);

  const COUNTRIES = {
    AU: "AU",
    US: "US",
    GB: "GB",
    IN: "IN",
  };

  const handleEventEmitter = ({ action, label = "", ...rest }: any) => {
    gtagEventEmitter({
      action,
      category: COMPONENTS_CATEGORY.TOP_NAV,
      label: `${PAGE.SEARCH} ${label}`,
      ...rest,
    });
  };

  const { t } = useTranslationHook();

  const handleZendeskClick = () => {
    handleEventEmitter({ action: QUICK_CHAT_CLICKED });

    // If Amber Copilot is enabled, open chatbot instead of zendesk
    if (isAmberCopilotEnabled) {
      openChatWidget({
        dispatch,
        isMobile: false,
        isDesktop: true,
        isAmberCopilotEnabled,
      });
      return;
    }

    if (isZendeskAfterLogin && !isUserLoggedIn && !isLFOneFilled) {
      addLoadZendeskParamInURL();
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "zendesk" });
    } else {
      openChatWidget({
        dispatch,
        isMobile: false,
        isDesktop: true,
        isAmberCopilotEnabled,
        entryPoint: FLOATING_BUTTON_ENTRY_POINTS.PAGE_BUTTON,
      });
    }
  };

  const showWhitelabelWeChatSupportQR = partner_id == "93557";

  const quickChatBadge = `<span class="${classes.quickChatBadge}">${t(
    "profile.support.components.supportlistitem.instantreply",
  )}</span>`;

  return (
    <div className={classes.subMenu}>
      <div className={classes.support}>
        <AgentContainer />
        <div className={classes.sectionSubtitle}>
          {t("home.herosection.support.platformsupport")}
        </div>
        {!showWhitelabelWeChatSupportQR ? (
          <div className={classes.supportItems}>
            {!isWhitelabel && !hideQuickChat && (
              <MenuItem
                label={t("home.herosection.support.quickchat")}
                icon={config.IS_CHINA_APP ? <ChatIcon /> : <SmartFilterLogo />}
                rightSection={quickChatBadge}
                clickHandler={handleZendeskClick}
                isDesktop
                isQuickChat
              />
            )}
            {config.IS_CHINA_APP ? (
              <MenuItem
                label={t("home.herosection.support.wechat")}
                icon={<WechatGreenOutline height={16} width={16} />}
                redirectTo={weChatSupportRedirectTo}
                clickHandler={() => handleEventEmitter({ action: WECHAT_CLICKED })}
                isDesktop
              />
            ) : (
              <>
                <MenuItem
                  label={t("home.herosection.support.whatsapp")}
                  icon={<WhatsAppColorFilled />}
                  redirectTo={WHATSAPP_URL}
                  clickHandler={() => handleEventEmitter({ action: WHATSAPP_CLICKED })}
                  isDesktop
                />
                {!isWhitelabel && (
                  <MenuItem
                    label={t("home.herosection.support.facebookmessenger")}
                    icon={<MessengerColorIcon fill="#0084FF" />}
                    redirectTo={FACEBOOK_MESSENGER_URL}
                    clickHandler={() => handleEventEmitter({ action: FACEBOOK_CLICKED })}
                    isDesktop
                  />
                )}
              </>
            )}
            <div className={classes.divider} />
            {(() => {
              const phoneMap: Record<string, { full_number: string }> = {
                [COUNTRIES.GB]: PHONE_1,
                [COUNTRIES.IN]: PHONE_2,
                [COUNTRIES.US]: PHONE_3,
                [COUNTRIES.AU]: PHONE_4,
              };
              const phone = phoneMap[country];
              return phone ? (
                <MenuItem
                  label={phone.full_number}
                  redirectTo={`tel:${phone.full_number}`}
                  icon={<PhoneIconColor width="16" height="16" fill="#3F83F8" />}
                  clickHandler={() => handleEventEmitter({ action: PHONE_CLICKED })}
                  isDesktop
                />
              ) : null;
            })()}
            <div className={classes.divider} />
            <MenuItem
              label={EMAIL_1}
              redirectTo={`mailto:${EMAIL_1}`}
              icon={<EnvelopeColor />}
              clickHandler={() => handleEventEmitter({ action: EMAIL_CLICKED })}
              isDesktop
            />
          </div>
        ) : (
          <div className={classes.wechatSupportQRContainer}>
            <Image
              src={getImagePath(
                "https://s3.ap-southeast-1.amazonaws.com/static-assets.amberstudent.com/assets/whitelabelwechatsupportqr.png",
              )}
              width={288}
              height={272}
            />
            <div className={classes.wechatSupportQRText}>
              <WeChatIcon /> {t("Use WeChat to scan for support.")}
            </div>
          </div>
        )}
      </div>
      {!isWhitelabel && (
        <>
          <div className={classes.seprator} />
          <div className={classes.quickLinksPanel}>
            <div className={formatClassNames(classes.sectionSubtitle, classes.quickLink)}>
              {t("home.herosection.support.quicklinks")}
            </div>
            <MenuItem
              label={t("home.herosection.support.helpcenter")}
              icon={<HowItWorks width={16} height={16} fill="#000" />}
              redirectTo={HELP_URL}
              clickHandler={() =>
                handleEventEmitter({ action: HELP_CENTRE_CLICKED, click_location: "support" })
              }
              isDesktop
            />
            <MenuItem
              label={t("home.herosection.support.howitworks")}
              icon={<HowItWorksIcon />}
              redirectTo={HOW_IT_WORKS_URL}
              clickHandler={() => handleEventEmitter({ action: HOW_IT_WORKS_CLICKED })}
              isDesktop
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Support;
